<script setup lang="ts">
import type { Component } from "vue";
import { markRaw, ref } from "vue";

import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { isCloud } from "@/utils/env/Environment";

import LoginPageEmail from "./internal/LoginPageEmail.vue";
import LoginRightPanel from "./internal/LoginRightPanel.vue";
import { useServicePopups } from "./internal/useServicePopups";

const currentLoginPage = ref<Component>(markRaw(LoginPageEmail));

const { showServices, openServices } = useServicePopups();

function pageSwitch(page: Component) {
  currentLoginPage.value = markRaw(page);
}
</script>

<template>
  <div class="login-page">
    <div v-if="showServices" class="services">
      <BaseButton data-t @click="openServices">Open services</BaseButton>
    </div>
    <div class="split left">
      <div class="form">
        <SvgIcon
          name="general/logo-named"
          class="piplanning-logo"
          :aria-label="$t('label.mainLogo')"
        />
        <component :is="currentLoginPage" @page-switch="pageSwitch" />
      </div>
      <div v-if="isCloud" class="footer">
        <I18nT keypath="userAgreement.base" scope="global">
          <template #termsOfService>
            <a href="https://www.piplanning.io/legal/terms-of-service" target="_blank">
              {{ $t("userAgreement.termsOfService") }}
            </a>
          </template>
          <template #privacyPolicy>
            <a href="https://scaledagile.com/privacy-policy/" target="_blank">
              {{ $t("userAgreement.privacyPolicy") }}
            </a>
          </template>
        </I18nT>
      </div>
    </div>

    <div class="split right">
      <LoginRightPanel />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";
@use "@/styles/z-index";

.services {
  position: absolute;
  z-index: z-index.$menu;
}

.split {
  height: 100%;
  width: 50%;
  position: fixed;
  top: 0;
  overflow-x: hidden;
}

.left {
  left: 0;
  background-color: colors.$background-ui;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1em;

  .piplanning-logo {
    width: 109px;
    height: 20px;
    margin-bottom: 36px;
    color: colors.$main;

    --logo-inner: #{colors.$background-ui};
  }

  .form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    width: 100%;
    max-width: 320px;
  }

  .footer {
    @include typography.extra-small("medium");

    color: colors.$text-subtle;
  }
}

.right {
  right: 0;
  overflow: hidden;
}
</style>
